<template>
    <div v-dark class="banner__container">
        <ExamDateModal v-if="showExamDateModal" @close="showExamDateModal = false" />
        <LeftBlob
            class="banner__blob-left"
            :class="`banner__blob-left--${scoreRange}`"
            :color="blobColor"
        />
        <RightBlob
            class="banner__blob-right"
            :class="`banner__blob-right--${scoreRange}`"
            :color="blobColor"
        />
        <div class="banner__heading">
            <h1 class="banner__title">
                Stats
            </h1>
            <div
                v-if="countdown"
                class="banner__countdown"
                tabindex="0"
                role="button"
                @click="showExamDateModal = true"
                @keydown.enter="showExamDateModal = true"
                @keydown.space.prevent="showExamDateModal = true"
                @mousedown.prevent
                @mouseover="isEditHovered = true"
                @mouseleave="isEditHovered = false"
                @focus="isEditHovered = true"
                @blur="isEditHovered = false"
            >
                <span class="banner__countdown-text">
                    <span v-if="countdown.circled" class="banner__countdown-days">
                        <CircleMarker
                            class="banner__countdown-marker"
                            :class="`banner__countdown-marker--${scoreRange}`"
                        />
                        {{ countdown.circled }}
                    </span>
                    {{ countdown.rest }}
                </span>
                <span class="banner__countdown-edit">
                    <Tooltip
                        v-if="isEditHovered"
                        class="banner__countdown-tooltip"
                        :is-dark-mode="true"
                    >
                        Edit Exam Date
                    </Tooltip>
                    <Icon
                        type="edit"
                        class="banner__countdown-pencil"
                        title="Edit Exam Date"
                    />
                </span>
            </div>
            <PocketLink
                v-else
                class="banner__countdown-link"
                :is-dark-mode="true"
                @click="showExamDateModal = true"
            >
                Add Exam Countdown
            </PocketLink>
        </div>
        <div class="banner">
            <Stat
                class="banner__correct"
                label="Answered Correctly"
                theme="dark"
            >
                <template #value>
                    {{ correctAnswers.length }}
                    <span>/</span>
                    {{ answers.length }}
                </template>
            </Stat>
            <Gauge
                :key="`${studyScore}-${communityScore}-${gaugeSize}`"
                class="banner__score"
                :score="studyScore"
                :community-score="communityScore"
                :size="gaugeSize"
                hide-community-label
                theme="dark"
            />
            <Stat
                class="banner__community"
                :value="`${communityScore || 0}%`"
                theme="dark"
            >
                <template #label>
                    <span class="banner__community-score-legend" />
                    Community
                </template>
            </Stat>
            <div class="banner__answer-stats">
                <div class="banner__answer-stat">
                    <div class="banner__answer-stat-number">
                        {{ correctAnswers.length }} / {{ answers.length }}
                    </div>
                    <div class="banner__answer-stat-label">
                        Answered Correctly
                    </div>
                </div>
                <div class="banner__community-stat">
                    <div class="banner__community-stat-number">
                        {{ communityScore || 0 }}%
                    </div>
                    <div class="banner__community-stat-label">
                        <span class="banner__community-score-legend" />
                        Community
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import LeftBlob from '@/assets/stats/LeftBlob.vue'
import RightBlob from '@/assets/stats/RightBlob.vue'
import CircleMarker from '@/assets/stats/CircleMarker.vue'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import UIKit from '@pocketprep/ui-kit'
import { quizModule } from '@/store/quiz/module'
import ExamDateModal from '@/components/ExamDateModal.vue'
import Gauge from '@/components/Gauge.vue'
import { userModule } from '@/store/user/module'
import { questionModule } from '@/store/question/module'
import { globalQuestionMetricModule } from '@/store/globalQuestionMetric/module'
import { screenModule } from '@/store/screen/module'
import Stat from '@/components/Stat.vue'
import { getScoreRange } from '@/utils'

const {
    Icon,
    Link: PocketLink,
    Tooltip,
} = UIKit

const showExamDateModal = ref(false)
const isEditHovered = ref(false)

const gaugeSize = computed(() => {
    const breakpoint = screenModule.getters.getBreakpoint()
    return breakpoint === 'black-bear' ? 'large' : 'medium'
})

const userExamMetadata = computed(() => {
    return userExamMetadataModule.getters.getCurrentUserExamMetadata()
})

const daysUntilExam = computed(() => {
    if (!userExamMetadata.value?.scheduledExamDate) {
        return undefined
    }

    const scheduledTime = new Date(userExamMetadata.value.scheduledExamDate.iso).getTime()
    const daysUntil = Math.ceil((scheduledTime - new Date().getTime()) / 86400000)

    return daysUntil
})

// the circled part of the countdown (if any), and the text that follows it
const countdown = computed(() => {
    if (daysUntilExam.value === undefined) {
        return null
    }
    // the exam date has passed, so show the date instead of counting down
    if (daysUntilExam.value < 0 && userExamMetadata.value?.scheduledExamDate) {
        const examDate = new Date(userExamMetadata.value.scheduledExamDate.iso)
        const formattedDate = examDate.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        })
        return { circled: null, rest: `Exam day: ${formattedDate}` }
    }
    if (daysUntilExam.value === 0) {
        return { circled: 'Today', rest: 'is the day! Good Luck 🤞' }
    }
    if (daysUntilExam.value === 1) {
        return { circled: 'Tomorrow', rest: 'is exam day' }
    }
    return { circled: `${daysUntilExam.value} days`, rest: 'to exam day' }
})

const answers = computed(() => {
    return Object.values(
        quizModule.getters.getLatestAnswers({ questionFilter: 'all' })
    )
})

const correctAnswers = computed(() => {
    return answers.value.filter((a) => {
        return a.isCorrect
    })
})

const studyScore = computed(() => {
    if (!answers.value.length) {
        return null
    }
    return Math.round((correctAnswers.value.length / answers.value.length) * 100)
})

// blobs and the countdown circle follow the score, and are yellow until there is one
const scoreRange = computed(() => {
    return studyScore.value === null ? 'medium' : getScoreRange(studyScore.value)
})

// the halftone dots of the blobs come in one image per color
const blobColor = computed(() => {
    return { low: 'red', medium: 'yellow', high: 'green' }[scoreRange.value] as 'red' | 'yellow' | 'green'
})

const communityScore = computed(() => {
    return globalQuestionMetricModule.getters.getExamCommunityAverage()
})

onMounted(async () => {
    await Promise.all([
        userModule.actions.fetchUserData(),
        questionModule.actions.fetchSerialQuestionInfoLib(),
        globalQuestionMetricModule.actions.fetchGlobalQuestionMetrics(),
    ])
})
</script>

<style lang="scss" scoped>
.banner {
    width: 943px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 165px;

    @include breakpoint(grizzly-bear) {
        margin-top: 145px;
    }

    @include breakpoint(brown-bear) {
        width: calc(100% - 22px);
        margin-left: 22px;
    }

    @include breakpoint(black-bear) {
        width: 100%;
        height: 100%;
        margin: 0 auto;
        justify-content: flex-start;
        flex-direction: column;
    }

    &__blob-left {
        position: absolute;
        left: 0;
        bottom: 0;

        &--low {
            color: $tomato;
        }

        &--medium {
            color: $butterscotch;
        }

        &--high {
            color: $jungle-green;
        }

        // on tablet only the halftone part of the left blob is shown
        @include breakpoint(grizzly-bear) {
            :deep(.left-blob__fill) {
                display: none;
            }
        }

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__blob-right {
        position: absolute;
        right: 0;
        top: 0;

        &--low {
            color: $tomato;
        }

        &--medium {
            color: $butterscotch;
        }

        &--high {
            color: $jungle-green;
        }
    }

    &__container {
        background: $moonlit-ocean;
        height: 255px;
        color: $white;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        position: relative;
        overflow: hidden;

        @include breakpoint(grizzly-bear) {
            height: 233px;
        }

        @include breakpoint(black-bear) {
            // the gauge and stat boxes fill the screen minus the 22px margins up to 500px wide, then stay centered
            --banner-gauge-width: min(100vw - 44px, 500px);

            // 397px with a 328px wide gauge, and it follows the height of the gauge as it scales
            height: calc(397px + var(--banner-gauge-width) * 0.4848 - 159px);
            display: block;
        }

        &--dark {
            background: $brand-black;
        }
    }

    &__correct {
        width: 180px;
        margin-right: 40px;
        align-self: flex-end;
        margin-bottom: 18px;

        span {
            color: $pewter;
            margin: 0 2px;
        }

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__community-score-legend {
        width: 7px;
        height: 7px;
        background: $fog;
        border-radius: 8px;
        display: inline-block;
        border: 1px solid $jet;
        box-sizing: border-box;
    }

    &__score {
        width: 260px;
        position: relative;

        :deep(.gauge:not(.gauge--large)) {
            width: 260px;
            height: 122px;
        }

        @include breakpoint(black-bear) {
            display: block;
            position: relative;
            left: auto;
            top: auto;
            margin: 181px auto 0;

            // fills the 22px margins up to 500px, and the gauge keeps its 328 x 159 shape as it scales
            width: var(--banner-gauge-width);

            :deep(.gauge--large) {
                width: 100%;
                height: auto;
                aspect-ratio: 328 / 159;
            }

            // the score scales with the gauge: 78px at 328px wide, and its line stays centered in the arc
            :deep(.gauge__score-percent--score.gauge__score-percent--large) {
                font-size: calc(var(--banner-gauge-width) * 0.2378);
                margin-top: calc(var(--banner-gauge-width) * 0.3674 - 70.5px);
            }

            // same for the empty state, 64px at 328px wide
            :deep(.gauge__score-percent--no-score) {
                font-size: calc(var(--banner-gauge-width) * 0.1951);
                margin-top: calc(var(--banner-gauge-width) * 0.3186 - 54.5px);
            }
        }
    }

    &__heading {
        position: absolute;
        top: 60px;
        left: calc(50% - 466px);
        z-index: 1;
        color: $white;

        @include breakpoint(grizzly-bear) {
            top: 36px;
        }

        @include breakpoint(brown-bear) {
            left: 43px;
        }

        @include breakpoint(black-bear) {
            left: 26px;
        }
    }

    &__title {
        margin: 0;
        font-size: 26px;
        line-height: 34px;
        font-weight: 600;
    }

    &__countdown {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 4px;
        font-size: 15px;
        line-height: 20px;
        font-weight: 500;
        color: $gray-background;
        cursor: pointer;
    }

    &__countdown-link {
        display: inline-block;
        margin-top: 6px;
        line-height: 20px;

        &:not(.uikit-link--disabled):hover {
            text-decoration: none;
        }
    }

    &__countdown-text {
        white-space: nowrap;
    }

    &__countdown-days {
        position: relative;
    }

    &__countdown-marker {
        position: absolute;
        top: 50%;
        right: calc(7.5% - 8px);
        width: calc((100% + 18px) * 0.9);
        height: 26.5px;
        transform: translateY(-50%);
        pointer-events: none;

        &--low {
            color: $tomato;
        }

        &--medium {
            color: $butterscotch;
        }

        &--high {
            color: $jungle-green;
        }
    }

    &__countdown-edit {
        position: relative;
        display: flex;

        .banner__countdown-tooltip {
            position: absolute;
            left: 50%;
            top: calc(-100% - 7px);
        }
    }

    &__countdown-pencil {
        width: 24px;
        height: 24px;
        color: $banana-bread;
    }

    &__container &__correct,
    &__container &__community {
        line-height: 33px;
    }

    &__correct :deep(.stat__label),
    &__community :deep(.stat__label) {
        color: $fog;
    }

    &__community .banner__community-score-legend {
        margin-right: 5px;
    }

    &__community {
        width: 140px;
        margin-left: 28px;
        align-self: flex-end;
        margin-bottom: 18px;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__answer-stats {
        display: none;
        justify-content: space-between;
        width: calc(100% - 44px);
        color: $white;

        @include breakpoint(black-bear) {
            display: flex;
            width: var(--banner-gauge-width);
            margin-right: auto;
            margin-left: auto;

            // 64px under a 328px wide gauge, and it follows the height of the gauge as it scales
            margin-top: calc(64px + var(--banner-gauge-width) * 0.4848 - 159px);
        }
    }

    &__answer-stat,
    &__community-stat {
        text-align: center;
        height: 69px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: $jet;
        border-radius: 6px;
        flex-direction: column;
        gap: 2px;
    }

    &__answer-stat {
        width: calc(61% - 6px);
    }

    &__community-stat {
        width: calc(39% - 6px);
        margin-left: auto;
    }

    &__answer-stat-number,
    &__community-stat-number {
        font-size: 26px;
        letter-spacing: 0.3px;
        font-weight: 600;
        line-height: 36px;
        height: 32px;
    }

    &__answer-stat-label,
    &__community-stat-label {
        font-size: 13px;
        font-weight: 500;
        line-height: 18px;
        color: $fog;
    }

    &__community-stat-label .banner__community-score-legend {
        margin-right: 5px;
    }
}
</style>
